export function getFilteredLuminaireLineProductsExt(context) {
    // Build LLT candidate lists from raw product groups + current LST/LLT state.
    // This is the frontend mirror of IP55 catalog scoping and attribute-based selection rules.

    const debug = (...args) => {
        try {
            if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production') {
                console.log(...args);
            }
        } catch (e) {}
    };

    if (!context.products || context.products === 'undefinded' || context.products.length < 1) {
        return null;
    }

    const subtypeOrder = [
        'rope_suspension_loop',
        'rope_suspension_carabiner',
        'rope_suspension_concrete_anchor',
        'rope_suspension_trapezoidal_sheet_hook',
        'suspension_hook'
    ];

    const isIp55Context = Number(context.selectedProtectionClass) === 15;
    const isEcoSeries = typeof context.selectedSerie === 'string' && context.selectedSerie.includes('eco');
    const isIp55Product = (product) => !!product && (product.ip55 === true || Number(product.ip55) === 1);
    const getPolicyBucketFromSerieId = (serieId) => {
        const numericSerieId = Number(serieId || 0);
        if (numericSerieId === 1 || numericSerieId === 44) return 'eco';
        if (numericSerieId === 2) return 'max_10';
        if (numericSerieId === 42) return 'max_12';
        if (numericSerieId === 43) return 'max_14';
        return null;
    };
    const getPolicyBucketFromSerieName = (serie) => {
        if (typeof serie !== 'string') return null;
        if (serie.includes('eco')) return 'eco';
        if (serie === 'max_10') return 'max_10';
        if (serie === 'max_12') return 'max_12';
        if (serie === 'max_14') return 'max_14';
        // Bare "max" can appear during partial hydration; default to max_12 so
        // we do not accidentally apply stricter max_10-only rules.
        if (serie.includes('max')) return 'max_12';
        return null;
    };
    const ip55PolicyBucket = getPolicyBucketFromSerieId(context?.lstForm?.led_module_serie_id)
        || getPolicyBucketFromSerieId(context?.planningFormFields?.led_module_serie_id)
        || getPolicyBucketFromSerieName(context.selectedSerie)
        || (isEcoSeries ? 'eco' : 'max_12');
    const ip55SeriesBucket = ip55PolicyBucket === 'eco' ? 'eco' : 'max';
    const matchesIp55SeriesBucket = (product) => {
        if (!product) return false;
        if (ip55SeriesBucket === 'eco') return product.serie === 'eco';
        return typeof product.serie === 'string' && product.serie.startsWith('max');
    };
    const isEnabledProduct = (product) => !!product
        && (product.enabled === true || Number(product.enabled) === 1 || typeof product.enabled === 'undefined');
    const getSharedConnectingCables = (products) => {
        const canonicalBySku = new Map();

        (Array.isArray(products) ? products : [])
            .filter(isEnabledProduct)
            .forEach(product => {
                const key = product && product.sku ? product.sku : `id:${product?.id || ''}`;
                const existing = canonicalBySku.get(key);

                // Keep one shared row per cable SKU and prefer the non-IP55 row as the canonical entry.
                if (!existing || (!isIp55Product(product) && isIp55Product(existing))) {
                    canonicalBySku.set(key, product);
                }
            });

        return Array.from(canonicalBySku.values())
            .sort((a, b) => String(a?.sku || '').localeCompare(String(b?.sku || '')));
    };
    // Prefer broadly applicable blind modules before serie-specific legacy rows.
    const sortBlindModulesBySpecificity = (products) => {
        return (Array.isArray(products) ? products : []).slice().sort((a, b) => {
            const serieRankA = a && !a.serie ? 0 : 1;
            const serieRankB = b && !b.serie ? 0 : 1;
            if (serieRankA !== serieRankB) {
                return serieRankA - serieRankB;
            }

            const optionCountA = Array.isArray(a?.luminaire_selection_options) ? a.luminaire_selection_options.length : 0;
            const optionCountB = Array.isArray(b?.luminaire_selection_options) ? b.luminaire_selection_options.length : 0;
            if (optionCountA !== optionCountB) {
                return optionCountB - optionCountA;
            }

            return Number(a?.id || 0) - Number(b?.id || 0);
        });
    };
    const isIp55StandaloneProduct = (product) => !!product
        && product.product_type === 'luminaire_control'
        && ['presence_detector', 'presence_detector_slave', 'intelligence'].includes(product.product_subtype)
        && isIp55Product(product);
    const hasHousingColorOption = (product) => {
        if (!context.selectedHousingColor || context.selectedHousingColor === 'undefined' || context.selectedHousingColor === '') {
            return true;
        }

        return !!(product.luminaire_selection_options || []).find(
            option => option.luminaire_selection_step_id === 2 && option.id === context.selectedHousingColor
        );
    };

    let suspensions = context.products.suspension?.filter(product => product.enabled)
        .sort(function (a, b) {
            if (a.size_x < b.size_x) {
                return -1;
            }
            if (a.size_x > b.size_x) {
                return 1;
            }
            return 0;
        })
        .sort((a, b) => {
            const indexA = subtypeOrder.indexOf(a.product_subtype);
            const indexB = subtypeOrder.indexOf(b.product_subtype);
            return indexA - indexB;
        });

    // No dependencies
    let warranties = context.products.warranty?.filter(product => product.enabled);
    let remote_controls = context.products.remote_control?.filter(product => product.enabled);
    let connecting_cable = getSharedConnectingCables(context.products.connecting_cable || []);
    let ceiling_mounts = context.products.ceiling_mount?.filter(product => product.enabled);
    let mounting_adapters = context.products.mounting_adapter?.filter(product => product.enabled);
    let blind_modules = sortBlindModulesBySpecificity(
        context.products.blind_module?.filter(product =>
            product.enabled
            && product.size_x === 168
            && (isIp55Context ? isIp55Product(product) : !isIp55Product(product))
        )
    );
    let luminaire_control_submodules = context.products.luminaire_control_submodule?.filter(product => product.enabled);


    // depending on serie. if serie empty at product, it is for all series
    let universal_connector_submodules = context.products.universal_connector_submodule?.filter(product => product.enabled
        && (!product.serie || context.selectedSerie.includes(product.serie))
    );

    // partly depending on serie AND control type
    let emergency_lights = context.products.emergency_light?.filter(product => product.enabled);

    // First check control type: ON/OFF (id: 27) = no emergency lights
    // DALI (id: 28) and CLO (id: 29) = show emergency lights based on serie
    if (context.selectedControl === 27) {
        // ON/OFF selected - no emergency lights
        emergency_lights = [];
    } else if (context.selectedControl === 28 || context.selectedControl === 29) {
        // DALI or CLO selected - filter by serie
        if (context.selectedSerie === 'eco') {
            // Eco: only battery emergency light (SKU 065534)
            emergency_lights = emergency_lights?.filter(product => product.sku === '065534');
        }
        // Max: show all emergency lights (no additional filter needed)
    }

    // depending on serie only
    let luminaire_controls = context.products.luminaire_control?.filter(product => product.enabled);

    let connecting_cable_middle = context.products.connecting_cable_middle?.filter(product => product.enabled
            && context.selectedSerie.includes(product.serie)
        )
        .sort(function (a, b) {
            if (a.size_x < b.size_x) {
                return -1;
            }
            if (a.size_x > b.size_x) {
                return 1;
            }
            return 0;
        });

    let universal_adapters = context.products.universal_adapter?.filter(product => product.enabled
        && !isIp55Product(product)
        && context.selectedSerie.includes(product.serie)
    ).sort(function (a, b) {
        if (a.title < b.title) {
            return -1;
        }
        if (a.title > b.title) {
            return 1;
        }
        return 0;
    });

    // depending on housing only
    let cable_routes = context.products.cable_route?.filter(product => product.enabled);
    let mount_connectors = context.products.mount_connector?.filter(product => product.enabled);

    if (context.selectedHousingColor
        && context.selectedHousingColor !== 'undefined'
        && context.selectedHousingColor !== ''
    ) {
        cable_routes = cable_routes?.filter(product => !!product.luminaire_selection_options.find(el => el.luminaire_selection_step_id === 2 && el.id === context.selectedHousingColor));
        mount_connectors = mount_connectors?.filter(product => !!product.luminaire_selection_options.find(el => el.luminaire_selection_step_id === 2 && el.id === context.selectedHousingColor));
    }

    // Filter presence detector controls AND intelligence controls by serie
    // luminaire_controls = standalone
    if (context.luminaireLineFormFields) {

        // Standalone products (presence detectors and intelligence) must also respect serie selection here
        luminaire_controls = luminaire_controls?.filter(product =>
            !(product.product_subtype?.includes("presence_detector") || product.product_subtype === "intelligence") ||
            (!context.selectedSerie || !product.serie || context.selectedSerie.includes(product.serie))
        );
    }

    // depening on Serie & Housing Color
    let electrical_feeds = context.products.electrical_feed?.filter(product => product.enabled);
    let mountingrails = context.products.mountingrail?.filter(product => product.enabled);
    let mountingrails_with_connectors = context.products.mountingrail_with_connectors?.filter(product => product.enabled);
    let universal_connectors = context.products.universal_connector?.filter(product => product.enabled);

    if (context.selectedSerie
        && context.selectedHousingColor) {

        electrical_feeds = electrical_feeds?.filter(product =>
            context.selectedSerie.includes(product.serie)
            && (isIp55Context ? isIp55Product(product) : !isIp55Product(product))
            && !!product.luminaire_selection_options.find(el => el.luminaire_selection_step_id === 2 && el.id === context.selectedHousingColor)
        );

        let mountingRailSubtype = 'mounting_rail_7_poles';
        if (context.selectedSerie === 'max_10') {
            mountingRailSubtype = 'mounting_rail_10_poles';
        } else if (context.selectedSerie === 'max_12') {
            mountingRailSubtype = 'mounting_rail_12_poles';
        } else if (context.selectedSerie === 'max_14') {
            mountingRailSubtype = 'mounting_rail_14_poles';
        }

        mountingrails = mountingrails?.filter(product =>
            context.selectedSerie.includes(product.serie)
            && product.product_subtype === mountingRailSubtype
            && (isIp55Context ? isIp55Product(product) : !isIp55Product(product))
            && !!product.luminaire_selection_options.find(el => el.luminaire_selection_step_id === 2 && el.id === context.selectedHousingColor)
        );

        console.log('IP55 mountingRailSubtype', mountingRailSubtype);
        console.log('IP55 context.selectedSerie', context.selectedSerie);
        console.log('IP55 isIp55Context', isIp55Context,)

        mountingrails_with_connectors = mountingrails_with_connectors?.filter(product =>
            context.selectedSerie.includes(product.serie)
            && (isIp55Context ? isIp55Product(product) : !isIp55Product(product))
            && !!product.luminaire_selection_options.find(el => el.luminaire_selection_step_id === 2 && el.id === context.selectedHousingColor)
        );

        // Allow serie-less products (apply to both Eco/Max). Presence detector standalone
        // variants are serie-less by design, so include them when product.serie is empty.
        luminaire_controls = luminaire_controls?.filter(product =>
                (!product.serie || context.selectedSerie.includes(product.serie))
                && !!product.luminaire_selection_options.find(el => el.luminaire_selection_step_id === 2 && el.id === context.selectedHousingColor)
            )
            .sort(function (a, b) {
                if (a.title < b.title) {
                    return -1;
                }
                if (a.title > b.title) {
                    return 1;
                }
                return 0;
            });

        universal_connectors = universal_connectors?.filter(product =>
            context.selectedSerie.includes(product.serie)
            && !!product.luminaire_selection_options.find(el => el.luminaire_selection_step_id === 2 && el.id === context.selectedHousingColor)
        );

    }


    if (isIp55Context) {
        // In IP55, standalone controls are resolved by product attributes + ip55 flag.
        luminaire_controls = (context.products.luminaire_control || [])
            .filter(isEnabledProduct)
            .filter(isIp55StandaloneProduct)
            .filter(matchesIp55SeriesBucket)
            .filter(product => hasHousingColorOption(product));
    } else {
        // Outside IP55, hide IP55-only standalone products from this family.
        luminaire_controls = (luminaire_controls || [])
            .filter(product => !(isIp55StandaloneProduct(product)));
    }


    // IP55 external accessories are resolved by DB attributes + ip55 flag.
    if (isIp55Context) {
        electrical_feeds = (context.products.electrical_feed || [])
            .filter(isEnabledProduct)
            .filter(isIp55Product)
            .filter(product => ['electrical_feed_start', 'electrical_feed_middle', 'electrical_feed_end'].includes(product.product_subtype))
            .filter(matchesIp55SeriesBucket)
            .filter(product => hasHousingColorOption(product));

        connecting_cable = getSharedConnectingCables(context.products.connecting_cable || []);

        connecting_cable_middle = [];

        universal_adapters = (context.products.universal_adapter || [])
            .filter(isEnabledProduct)
            .filter(isIp55Product)
            .filter(product => ['universal_adapter_sm', 'universal_adapter_lg'].includes(product.product_subtype));

        luminaire_control_submodules = [];
        universal_connector_submodules = [];
    }

    // DALI separators are modeled under `addon` products in this catalog.
    // IP55 uses subtype+serie+ip55 filtering; non-IP55 keeps legacy subtype+serie filtering.
    let dali_separators = [];
    if (context.products.addon && context.selectedSerie) {
        if (isIp55Context) {
            if (ip55PolicyBucket !== 'max_10') {
                dali_separators = context.products.addon.filter(product =>
                    isEnabledProduct(product)
                    && isIp55Product(product)
                    && product.product_subtype === 'dali_separator'
                    && matchesIp55SeriesBucket(product)
                );
            }
        } else {
            dali_separators = context.products.addon.filter(product =>
                product.enabled &&
                !isIp55Product(product) &&
                product.product_subtype === 'dali_separator' &&
                context.selectedSerie.includes(product.serie)
            );
        }
    }

    // depending on Optic

    let optics = null;
    let individual_optics = [];

    const knownCombinedOpticIds = [7, 37, 8, 6, 5, 41];

    if (context.selectedOptics === 7 || context.selectedOptics === 37) {
        // ASY links = 7 & rechts = 37
        optics = context.products.optic_asy;
    } else if (context.selectedOptics === 8) {
        // DASY = 8
        optics = context.products.optic_dasy;
    } else if (context.selectedOptics === 6) {
        // BATWING = 6
        optics = context.products.optic_batwing;
    } else if (context.selectedOptics === 5) {
        // INTENSE = 5
        optics = context.products.optic_intense;
    } else if (context.selectedOptics === 41) {
        // OPAL = 41
        optics = context.products.optic_opal;
    } else if (context.selectedOptics && !knownCombinedOpticIds.includes(Number(context.selectedOptics))) {
        // Individual optics or unknown IDs are handled server-side; keep optics empty to avoid mismatched products
        optics = [];
    }

    // Expose individual optics products separately (serie-independent, enabled)
    if (context.products.individual_optic && context.products.individual_optic.length) {
        individual_optics = context.products.individual_optic.filter(product => product.enabled);
    }

    if (context.products.optic && context.products.optic.length) {
        optics = context.products.optic.filter(product => product && product.enabled);
    }

    if (isIp55Context) {
        if (![41, 6, 5].includes(Number(context.selectedOptics))) {
            optics = [];
        } else if (Array.isArray(optics)) {
            optics = optics.filter(isIp55Product);
        }

        individual_optics = [];
    } else if (Array.isArray(optics)) {
        // Exclude IP55-only optics from non-IP55 context.
        optics = optics.filter(product => !isIp55Product(product));
    }

    // depending on protection class. IP 44 = 46, IP 55 = 15, IP 65 = 16, IP 40 = 14
    // IP55 (id 15) no longer uses separate protection_class add-on products.
    let protection_class = null;
    if (Number(context.selectedProtectionClass) === 16) {
        protection_class = context.products.protection_class.filter(product => product.product_subtype === 'protection_class_ip65')
    } else {
        protection_class = null;
    }

    // End cap seals only relevant for IP44
    let end_cap_seals = [];
    if (Number(context.selectedProtectionClass) === 46) { // IP44 option id
        end_cap_seals = context.products.end_cap_seal?.filter(product => product.enabled)
            ?.filter(product => {
                if (!context.selectedSerie) return false;
                if (context.selectedSerie === 'eco') return product.serie === 'eco';
                return product.serie && product.serie.startsWith('max');
            }) || [];
    }

    return {
        ceiling_mounts,
        emergency_lights,
        luminaire_controls,
        luminaire_control_submodules,
        suspensions,
        universal_adapters,
        mounting_adapters,
        cable_routes,
        blind_modules,
        mount_connectors,
        electrical_feeds,
        mountingrails,
        mountingrails_with_connectors,
        warranties,
        remote_controls,
        universal_connectors,
        universal_connector_submodules,
        optics,
        dali_separators,
        connecting_cable,
        connecting_cable_middle,
        protection_class,
        end_cap_seals,
        individual_optics
    };


}
